import { useEffect } from 'react';
import { Box } from '@chakra-ui/react';
import { Colors, Text } from '@mercator-gcp/grout';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import BasePageWithTopNav from 'components/layout/TopNavigationLayout';
import { useSendVerificationEmail } from 'hooks/useSendVerificationEmail';
import { useToast } from 'hooks/useToast';
import { useUser } from 'state/auth';
import { trackPageView } from 'utils/analytics';

const parentColumn = {
  width: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
};

const verifyContainer = {
  maxWidth: '450px',
};

const verificationLink = {
  color: Colors.ForestGreen900,
  display: 'inline',
  marginLeft: 'padding_4',
  _hover: {
    cursor: 'pointer',
    textDecoration: 'underline',
  },
};

const alreadyLoggedInCTAStyle = {
  marginTop: 'padding_24',
  columnGap: 'padding_4',
  justifyContent: 'flex-start',
};

const EmailVerification = () => {
  const { user, isUserEmailVerified } = useUser();
  const sendVerificationEmail = useSendVerificationEmail();
  const { renderLoadingToast, closeToast } = useToast();

  useEffect(() => {
    trackPageView({ sectionName: 'sign_up', subSectionName: 'sign_up_verify_email' });
  }, []);

  const handleResendVerification = async () => {
    if (sendVerificationEmail.isLoading) {
      return;
    }

    let toastId;

    try {
      if (user && !isUserEmailVerified) {
        toastId = renderLoadingToast({
          toastTitle: 'Sending verification email',
          label: 'Sending verification email...',
        });
        await sendVerificationEmail.mutateAsync();
        closeToast(toastId);
      }
    } catch (_) {
      if (toastId) {
        closeToast(toastId);
      }
    }
  };

  return (
    <BasePageWithTopNav>
      <FlexColumn sx={parentColumn}>
        <Box sx={verifyContainer}>
          <Text.H3>Verify your account</Text.H3>

          <Text.Body>
            We’ve sent a link to <Text.BodyBold>{user?.email}</Text.BodyBold> to verify
            your account. If you can’t find your email, please check your spam folder or
            <Box as="span" sx={verificationLink} onClick={handleResendVerification}>
              click here to resend.
            </Box>
          </Text.Body>

          <FlexRow sx={alreadyLoggedInCTAStyle}>
            <Text>Once verified, you will be redirected.</Text>
          </FlexRow>
        </Box>
      </FlexColumn>
    </BasePageWithTopNav>
  );
};

export default EmailVerification;
